import { useMemo } from 'react';

export interface ProductItem {
  id: number;
  productName: string;
  productCode: string;
  quantity: string;
  productId?: number;
  originalItemId?: number;
}

export interface LocationData {
  id: number;
  name: string;
}

export interface ProductData {
  id: number;
  name: string;
  code: string;
  material?: {
    id: number;
    name: string;
    description?: string;
  };
  weightGrams?: string;
  lengthMm?: string;
  diameterSize?: string;
  color?: string;
  workshopCode?: string;
  costPrice?: string;
  salePrice?: string;
}

export interface ProductUnit {
  id: string;
  productId: number;
  productName: string;
  productCode: string;
  quantity: number;
  properties: {
    materialId: string;
    materialName: string;
    weight: string;
    length: string;
    diameter: string;
    color: string;
    workshopCode: string;
  };
  costs: {
    costPrice: string;
    salePrice: string;
  };
}

export interface UseConfirmationStepProps {
  productItems: ProductItem[];
  selectedBranch: string; // This is a location ID
  products: ProductData[];
  locations?: LocationData[];
  productUnits?: ProductUnit[];
}

export const useConfirmationStep = ({
  productItems,
  selectedBranch: selectedLocationId,
  products,
  locations = [],
  productUnits = [],
}: UseConfirmationStepProps) => {
  // Calcular cantidad total
  const totalQuantity = useMemo(() => {
    return productItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  }, [productItems]);

  // Calcular totales de costos desde las unidades editadas
  const totalCostPrice = useMemo(() => {
    return productUnits.reduce((sum, unit) => {
      const costPrice = parseFloat(unit.costs.costPrice) || 0;
      return sum + costPrice * unit.quantity;
    }, 0);
  }, [productUnits]);

  const totalSalePrice = useMemo(() => {
    return productUnits.reduce((sum, unit) => {
      const salePrice = parseFloat(unit.costs.salePrice) || 0;
      return sum + salePrice * unit.quantity;
    }, 0);
  }, [productUnits]);

  // Formatear fecha en español con hora
  const formattedDate = useMemo(() => {
    return new Date().toLocaleString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  // Encontrar nombre de ubicación desde el ID
  const locationName = useMemo(() => {
    const location = locations.find((loc) => loc.id.toString() === selectedLocationId);
    return location ? location.name : 'Ubicación no encontrada';
  }, [locations, selectedLocationId]);

  // Obtener productos con detalles completos
  const productsWithDetails = useMemo(() => {
    return productItems.map((item) => {
      const productDetail = products.find((p) => p.id === item.productId);
      return {
        ...item,
        productDetail: productDetail || null,
      };
    });
  }, [productItems, products]);

  // Calcular estadísticas de productos
  const productStats = useMemo(() => {
    const validItems = productItems.filter(
      (item) => item.productName && item.productCode && item.quantity && Number(item.quantity) > 0,
    );

    const uniqueProducts = new Set(validItems.map((item) => item.productId)).size;

    return {
      totalItems: productItems.length,
      validItems: validItems.length,
      invalidItems: productItems.length - validItems.length,
      uniqueProducts,
      totalQuantity,
      isValid: validItems.length === productItems.length && productItems.length > 0,
    };
  }, [productItems, totalQuantity]);

  // Obtener resumen por producto
  const productSummary = useMemo(() => {
    const summary = new Map<
      number,
      {
        product: ProductData | null;
        totalQuantity: number;
        items: ProductItem[];
      }
    >();

    productItems.forEach((item) => {
      if (item.productId) {
        const existing = summary.get(item.productId);
        const productDetail = products.find((p) => p.id === item.productId);
        const quantity = Number(item.quantity || 0);

        if (existing) {
          existing.totalQuantity += quantity;
          existing.items.push(item);
        } else {
          summary.set(item.productId, {
            product: productDetail || null,
            totalQuantity: quantity,
            items: [item],
          });
        }
      }
    });

    return Array.from(summary.values());
  }, [productItems, products]);

  // Determinar si hay datos válidos
  const hasValidData = useMemo(() => {
    return productStats.isValid && selectedLocationId && locations.length > 0;
  }, [productStats.isValid, selectedLocationId, locations.length]);

  // Obtener mensajes de validación
  const validationMessages = useMemo(() => {
    const messages: string[] = [];

    if (productItems.length === 0) {
      messages.push('No hay productos seleccionados');
    } else if (!productStats.isValid) {
      messages.push(`${productStats.invalidItems} productos tienen datos incompletos`);
    }

    if (!selectedLocationId) {
      messages.push('No se ha seleccionado una ubicación');
    }

    if (locations.length === 0) {
      messages.push('No hay ubicaciones disponibles');
    }

    return messages;
  }, [productItems.length, productStats, selectedLocationId, locations.length]);

  return {
    totalQuantity,
    totalCostPrice,
    totalSalePrice,
    formattedDate,
    locationName,
    productsWithDetails,
    productStats,
    productSummary,
    hasValidData,
    validationMessages,
    productItems,
    selectedLocationId,
    products,
    locations,
  };
};
